
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { FileText, ExternalLink, Upload, Loader2, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { FullLicenseText } from "@/components/licensing/FullLicenseText";
import { LicenseVariables, MultiLingualContent, getLocalizedContent, createDefaultLicenseVariables } from "@/types/licensing";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { generateLicensePDF } from "@/utils/pdf/licensePdfGenerator";

interface Download {
  id: string;
  clipId: string;
  clipName: string;
  thumbnailUrl: string;
  licenseId: string;
  licenseName: string;
  downloadDate: string;
  exactDownloadTimestamp: string;
  licenseVariables: LicenseVariables;
  permissions?: MultiLingualContent[] | string[];
  restrictions?: MultiLingualContent[] | string[];
}

const ITEMS_PER_PAGE = 10;

const DownloadHistory: React.FC = () => {
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<{
    download: Download;
    variables: LicenseVariables;
    permissions?: MultiLingualContent[] | string[];
    restrictions?: MultiLingualContent[] | string[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isMobile = useIsMobile();

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  useEffect(() => {
    fetchDownloads(currentPage);
  }, [user, language, currentPage]);

  async function fetchDownloads(page: number) {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      const offset = (page - 1) * ITEMS_PER_PAGE;
      
      // First get the total count
      const { count, error: countError } = await supabase
        .from('downloads')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      
      if (countError) {
        console.error("Error counting downloads:", countError);
        toast.error("Failed to load download count");
        return;
      }
      
      setTotalCount(count || 0);
      
      // Then get the paginated data
      const { data, error } = await supabase
        .from('downloads')
        .select('*')
        .eq('user_id', user.id)
        .order('downloaded_at', { ascending: false })
        .range(offset, offset + ITEMS_PER_PAGE - 1);
      
      if (error) {
        console.error("Error fetching downloads:", error);
        toast.error("Failed to load download history");
        return;
      }
      
      const formattedDownloads: Download[] = [];
      
      for (const item of data) {
        const { data: clipData, error: clipError } = await supabase
          .from('clips')
          .select('license_id, title, thumbnail')
          .eq('id', item.clip_id)
          .maybeSingle();
        
        if (clipError) {
          console.error("Error fetching clip:", clipError);
          continue;
        }
        
        let licenseData = {
          name: "Standard License",
          rights_holder: "Pathé",
          mentions: ["pathefilms", "recut.art"],
          hashtags: ["recut"],
          permissions: [],
          restrictions: []
        };
        
        if (clipData?.license_id) {
          const { data: license, error: licenseError } = await supabase
            .from('licenses')
            .select('*')
            .eq('id', clipData.license_id)
            .maybeSingle();
            
          if (!licenseError && license) {
            licenseData = {
              name: license.name,
              rights_holder: license.rights_holder,
              mentions: license.mentions || [],
              hashtags: license.hashtags || [],
              permissions: license.permissions || [],
              restrictions: license.restrictions || []
            };
          }
        }
        
        formattedDownloads.push({
          id: item.id,
          clipId: item.clip_id,
          clipName: item.clip_title,
          thumbnailUrl: clipData?.thumbnail || "/placeholder.svg",
          licenseId: clipData?.license_id || "default",
          licenseName: licenseData.name,
          downloadDate: new Date(item.downloaded_at).toLocaleDateString(
            language === 'en' ? 'en-US' : 'fr-FR',
            { year: 'numeric', month: 'long', day: 'numeric' }
          ),
          exactDownloadTimestamp: item.downloaded_at,
          licenseVariables: createDefaultLicenseVariables({
            rightsholder: licenseData.rights_holder,
            mentions: licenseData.mentions,
            hashtags: licenseData.hashtags
          }),
          permissions: licenseData.permissions,
          restrictions: licenseData.restrictions
        });
      }
      
      setDownloads(formattedDownloads);
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  }

  const openLicenseDetails = (download: Download) => {
    setSelectedLicense({
      download,
      variables: download.licenseVariables,
      permissions: download.permissions,
      restrictions: download.restrictions
    });
    setLicenseDialogOpen(true);
  };

  const handleCreateDerivative = (clipId: string) => {
    navigate(`/create-derivative?clipId=${clipId}`);
  };

  const convertToMultiLingual = (items: any[] = []): MultiLingualContent[] => {
    if (items.length === 0) return [];
    
    if (typeof items[0] === 'object' && 'text' in items[0] && 'language' in items[0]) {
      return items as MultiLingualContent[];
    }
    
    if (typeof items[0] === 'string' && items[0].includes('|')) {
      return items.map((item: string) => {
        const [text, lang] = item.split('|');
        return { text, language: lang as 'en' | 'fr' };
      });
    }
    
    if (typeof items[0] === 'string') {
      return items.map((text: string) => ({ text, language: 'en' }));
    }
    
    return [];
  };

  const downloadLicenseAsPDF = async () => {
    if (!selectedLicense || !user) {
      toast.error(language === 'en' ? "No license selected" : "Aucune licence sélectionnée");
      return;
    }
    
    try {
      setIsGeneratingPdf(true);
      console.log("Starting PDF generation for historical license");
      
      // Validate required data
      if (!selectedLicense.download || !selectedLicense.variables) {
        throw new Error("Missing license data");
      }
      
      // Convert permissions and restrictions to localized content
      const permissions = selectedLicense.permissions 
        ? getLocalizedContent(convertToMultiLingual(selectedLicense.permissions), language) 
        : [];
      const restrictions = selectedLicense.restrictions 
        ? getLocalizedContent(convertToMultiLingual(selectedLicense.restrictions), language) 
        : [];
      
      // Get user name safely
      const userName = user?.user_metadata?.full_name || user?.email || 'N/A';
      
      // Use the new PDF generator utility
      await generateLicensePDF(
        {
          clipName: selectedLicense.download.clipName,
          downloadDate: selectedLicense.download.downloadDate,
          exactDownloadTimestamp: selectedLicense.download.exactDownloadTimestamp,
          licenseName: selectedLicense.download.licenseName,
          licenseVariables: selectedLicense.variables,
          permissions,
          restrictions
        },
        userName,
        language
      );
      
      console.log("Historical license PDF generated successfully");
      toast.success(language === 'en' ? "Historical license certificate downloaded as PDF" : "Certificat de licence historique téléchargé en PDF");
      
    } catch (error) {
      console.error("Error generating historical license PDF:", error);
      toast.error(language === 'en' ? "Failed to generate license PDF. Please try again." : "Échec de génération du PDF de licence. Veuillez réessayer.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
        <span>Loading downloads...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className={`${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}>
          <CardTitle>Download History</CardTitle>
          <CardDescription className="text-sm">
            Clips you've downloaded from the platform ({totalCount} total)
          </CardDescription>
        </CardHeader>
        <CardContent className={`${isMobile ? 'px-3 py-2' : 'px-6 py-4'}`}>
          {downloads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>You haven't downloaded any clips yet.</p>
            </div>
          ) : isMobile ? (
            <div className="space-y-3">
              {downloads.map((download) => (
                <Card key={download.id} className="overflow-hidden">
                  <div className="p-3 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-sm">{download.clipName}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          Downloaded: {download.downloadDate}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openLicenseDetails(download)}
                        className="h-8 text-xs w-full"
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" />
                        License Terms
                      </Button>
                      <div className="flex w-full gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild
                          className="h-8 text-xs flex-1"
                        >
                          <a href={`/clip/${download.clipId}`}>
                            <ExternalLink className="h-3.5 w-3.5 mr-1" />
                            View Clip
                          </a>
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => handleCreateDerivative(download.clipId)}
                          className="h-8 text-xs flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                        >
                          <Upload className="h-3.5 w-3.5 mr-1" />
                          Upload
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clip</TableHead>
                  <TableHead>Download Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {downloads.map((download) => (
                  <TableRow key={download.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <span className="font-medium">{download.clipName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{download.downloadDate}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openLicenseDetails(download)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          License Terms
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a href={`/clip/${download.clipId}`} className="flex items-center">
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View Clip
                          </a>
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => handleCreateDerivative(download.clipId)}
                          className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Upload Derivative
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="gap-1 pl-2.5"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                  </PaginationItem>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and adjacent pages
                    if (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    ) {
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => handlePageChange(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    }
                    
                    // Show ellipsis for gaps
                    if (page === 2 && currentPage > 4) {
                      return (
                        <PaginationItem key="ellipsis-start">
                          <span className="flex h-9 w-9 items-center justify-center text-sm">...</span>
                        </PaginationItem>
                      );
                    }
                    
                    if (page === totalPages - 1 && currentPage < totalPages - 3) {
                      return (
                        <PaginationItem key="ellipsis-end">
                          <span className="flex h-9 w-9 items-center justify-center text-sm">...</span>
                        </PaginationItem>
                      );
                    }
                    
                    return null;
                  })}
                  
                  <PaginationItem>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="gap-1 pr-2.5"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={licenseDialogOpen} onOpenChange={setLicenseDialogOpen}>
        <DialogContent className={`max-w-3xl ${isMobile ? 'p-3 max-h-[90vh]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>License Terms & Requirements</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={downloadLicenseAsPDF}
                disabled={isGeneratingPdf}
                className="ml-4"
              >
                {isGeneratingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    {language === 'en' ? 'Generating...' : 'Génération...'}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1" />
                    {language === 'en' ? 'Download Historical PDF' : 'Télécharger PDF Historique'}
                  </>
                )}
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className={`${isMobile ? 'max-h-[65vh]' : 'max-h-[70vh]'} overflow-y-auto pr-1`}>
            {selectedLicense && (
              <div className="license-content-for-pdf">
                <FullLicenseText 
                  licenseVariables={selectedLicense.variables}
                  permissions={selectedLicense.permissions ? 
                    getLocalizedContent(convertToMultiLingual(selectedLicense.permissions), language) : 
                    []}
                  restrictions={selectedLicense.restrictions ? 
                    getLocalizedContent(convertToMultiLingual(selectedLicense.restrictions), language) : 
                    []}
                  condensed={true}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DownloadHistory;
